import { useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { useCallback, useState } from 'react';

import { createChart } from '@/entities/chart/api';
import type {
  BaseFormValues,
  DashboardTemplate,
} from '@/entities/dashboard-template';
import { DASHBOARD_LIST_QUERY_KEY } from '@/features/Dashboard/hooks/useDashboardListQuery';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import dashboardService from '@/services/dashboard2';

import { buildChartPayloads } from './build-chart-payloads';
import { buildDashboardLayout } from './build-dashboard-layout';
import {
  CREATION_STEP,
  CREATION_STEP_STATUS,
  type CreationStepState,
} from './types';

const INITIAL_STEPS: CreationStepState[] = [
  {
    step: CREATION_STEP.DASHBOARD,
    status: CREATION_STEP_STATUS.PENDING,
    label: 'Creating dashboard',
  },
  {
    step: CREATION_STEP.CHARTS,
    status: CREATION_STEP_STATUS.PENDING,
    label: 'Creating charts',
  },
  {
    step: CREATION_STEP.LAYOUT,
    status: CREATION_STEP_STATUS.PENDING,
    label: 'Building layout',
  },
];

/**
 * 대시보드 템플릿에서 대시보드를 생성하는 오케스트레이션 훅
 *
 * 생성 시퀀스:
 * 1. createDashboard(title) → Dashboard2T { alias }
 * 2. Promise.allSettled(charts.map(createChart)) → ChartItem[] { alias }
 * 3. Promise.allSettled(rows.map(addDashboardRow)) → row_id[]
 * 4. navigate(/dashboard2/{alias})
 */
export const useCreateDashboardFromTemplate = () => {
  const queryClient = useQueryClient();
  const wr = useWorkspaceRouter();
  const [steps, setSteps] = useState<CreationStepState[]>(INITIAL_STEPS);
  const [isCreating, setIsCreating] = useState(false);

  const updateStepStatus = useCallback(
    (step: CreationStepState['step'], status: CreationStepState['status']) => {
      setSteps((prev) =>
        prev.map((s) => (s.step === step ? { ...s, status } : s)),
      );
    },
    [],
  );

  const create = useCallback(
    async <TFormValues extends BaseFormValues>(
      template: DashboardTemplate<TFormValues>,
      formValues: TFormValues,
    ) => {
      setIsCreating(true);
      setSteps(INITIAL_STEPS);

      try {
        // Step 1: 대시보드 생성
        updateStepStatus(
          CREATION_STEP.DASHBOARD,
          CREATION_STEP_STATUS.IN_PROGRESS,
        );

        const dashboard = await dashboardService.createDashboard(
          formValues.title,
        );

        updateStepStatus(
          CREATION_STEP.DASHBOARD,
          CREATION_STEP_STATUS.COMPLETED,
        );

        // Step 2: 차트 생성 (병렬, 부분 실패 허용)
        updateStepStatus(
          CREATION_STEP.CHARTS,
          CREATION_STEP_STATUS.IN_PROGRESS,
        );

        const sectionFormValuesList: TFormValues[] = [formValues];

        const sectionChartPayloads = sectionFormValuesList.map(
          (sectionFormValues) =>
            buildChartPayloads(template, sectionFormValues),
        );

        const chartPayloads = sectionChartPayloads.flat();
        const chartResults = await Promise.allSettled(
          chartPayloads.map((payload) => createChart(payload)),
        );

        // 성공한 차트의 alias 수집 (실패한 것은 null)
        const chartAliases: (string | null)[] = chartResults.map((result) =>
          result.status === 'fulfilled' ? result.value.alias : null,
        );

        const successCount = chartAliases.filter(Boolean).length;
        const failCount = chartAliases.length - successCount;

        if (failCount > 0 && successCount > 0) {
          message.warning(
            `${failCount} out of ${chartAliases.length} charts failed to create`,
          );
        }

        if (successCount === 0) {
          updateStepStatus(CREATION_STEP.CHARTS, CREATION_STEP_STATUS.FAILED);
          message.error('Failed to create any charts');
          setIsCreating(false);
          // 빈 대시보드라도 리다이렉트
          queryClient.invalidateQueries({ queryKey: [DASHBOARD_LIST_QUERY_KEY] });
          wr.push(`/dashboard2/${dashboard.alias}`);
          return;
        }

        updateStepStatus(CREATION_STEP.CHARTS, CREATION_STEP_STATUS.COMPLETED);

        // Step 3: 레이아웃 행 추가 (순차 실행으로 순서 보장)
        updateStepStatus(
          CREATION_STEP.LAYOUT,
          CREATION_STEP_STATUS.IN_PROGRESS,
        );

        let chartAliasCursor = 0;
        const layoutParamsWithSectionPos = sectionFormValuesList.flatMap(
          (sectionFormValues, sectionIndex) => {
            const sectionChartCount = sectionChartPayloads[sectionIndex]?.length ?? 0;
            const sectionChartAliases = chartAliases.slice(
              chartAliasCursor,
              chartAliasCursor + sectionChartCount,
            );
            chartAliasCursor += sectionChartCount;

            return buildDashboardLayout(
              template,
              sectionChartAliases,
              sectionFormValues,
            );
          },
        );

        const layoutParams = layoutParamsWithSectionPos.map((row, index) => ({
          ...row,
          pos: index * 1000,
        }));
        let layoutFailCount = 0;
        for (const params of layoutParams) {
          try {
            const { height, ...addRowParams } = params;
            const createdRow = await dashboardService.addDashboardRow(
              dashboard.alias,
              addRowParams,
            );

            if (height) {
              await dashboardService.changeDashboardRowHeight(
                dashboard.alias,
                createdRow.row_id,
                height,
              );
            }
          } catch {
            layoutFailCount++;
          }
        }

        if (layoutFailCount > 0) {
          message.warning(`${layoutFailCount} layout rows failed to create`);
          updateStepStatus(CREATION_STEP.LAYOUT, CREATION_STEP_STATUS.FAILED);
        } else {
          updateStepStatus(
            CREATION_STEP.LAYOUT,
            CREATION_STEP_STATUS.COMPLETED,
          );
        }

        // Step 4: 리다이렉트
        queryClient.invalidateQueries({ queryKey: [DASHBOARD_LIST_QUERY_KEY] });
        wr.push(`/dashboard2/${dashboard.alias}`);
      } catch (error) {
        message.error('Failed to create dashboard');
        // 실패한 현재 step 표시
        setSteps((prev) =>
          prev.map((s) =>
            s.status === CREATION_STEP_STATUS.IN_PROGRESS
              ? { ...s, status: CREATION_STEP_STATUS.FAILED }
              : s,
          ),
        );
      } finally {
        setIsCreating(false);
      }
    },
    [queryClient, wr, updateStepStatus],
  );

  return {
    create,
    steps,
    isCreating,
  };
};
